import { Button } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import { FileLimitationEnum } from '../../../enums/FileLimitationEnum';
import type FailedFile from '../../../interfaces/FailedFile';

/**
 * Maps API / limitation codes to translation keys for failed move reasons.
 */
const FAIL_REASON_KEYS: Record<string, string> = {
  [ApiErrorEnum.FolderPasswordRequired]: 'errors.folderPasswordRequired',
  [ApiErrorEnum.CannotAccessFiles]: 'errors.cannotAccessFiles',
  [FileLimitationEnum.DenyFileMove]: 'errors.denyFileMove',
  MoveFileFailed: 'activityLogOperationTypes.MoveFileFailed',
};

interface FilesMoveErrorFormProps {
  /** Files that could not be moved, with error reasons from the API. */
  failedFilesList?: FailedFile[];
  /** Handler to close/dismiss the error form. */
  onClose?: () => void;
}

/**
 * Lists files excluded from a move operation with per-file failure reasons.
 */
export const FilesMoveErrorForm = ({ failedFilesList, onClose }: FilesMoveErrorFormProps) => {
  const { t } = useTranslation();

  return (
    <div className="pt-2">
      <span className="text-gray-dark text-sm">{t('common.filesExcludedForMove')}</span>
      <div className="divide-gray-medium border-gray-medium mt-4 flex max-h-80 flex-col gap-2 divide-y overflow-auto border-y">
        {failedFilesList?.map((failedFile) => {
          const reasonKey = FAIL_REASON_KEYS[failedFile.lastError];
          return (
            <div key={failedFile.fileId} className="flex flex-col gap-1 px-1 py-1.5">
              <span className="text-blue-dark truncate" title={failedFile.fileName}>
                {failedFile.fileName}
              </span>
              <span className="text-red-dark text-xs">
                {reasonKey ? t(reasonKey) : failedFile.lastError || t('common.failed')}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex flex-row items-center justify-end gap-2">
        <Button size="middle" type="primary" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>
    </div>
  );
};

export default FilesMoveErrorForm;
